import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Award, Globe, GraduationCap, Scale, Target, Users } from 'lucide-react';

const About = () => {
  const { t, language } = useLanguage();
  const stats = t('about.stats') || [];
  const statIcons = [Award, Scale, Globe, GraduationCap];

  const labels = {
    en: { aboutUs: 'About Us' },
    tr: { aboutUs: 'Kurumsal' },
    es: { aboutUs: 'Sobre Nosotros' },
    fr: { aboutUs: 'À Propos' },
  };

  const currentLabels = labels[language] || labels.en;

  return (
    <section id="about" className="relative bg-[#f5f7fa] py-24 lg:py-32 overflow-hidden">
      {/* Background Accent Image */}
      <div className="absolute bottom-0 left-0 w-1/2 h-1/2 opacity-[0.03]">
        <img 
          src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&auto=format&fit=crop&q=60"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Section Label */}
            <span className="text-[#1e3a5f] font-semibold text-sm uppercase tracking-wider">{currentLabels.aboutUs}</span>

            {/* Title */}
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1e3a5f] leading-tight" style={{ fontFamily: "'Cardo', Georgia, serif" }}>
              {t('about.title')}
            </h2>

            {/* Subtitle */}
            <p className="text-xl text-[#3d5a80] font-medium">
              {t('about.subtitle')}
            </p>

            {/* Description */}
            <p className="text-gray-600 text-lg leading-relaxed">
              {t('about.description')}
            </p>

            {/* Story */}
            <p className="text-gray-600 leading-relaxed">
              {t('about.story')}
            </p>

            {/* Mission Quote */}
            <div className="bg-white rounded-xl p-6 border-l-4 border-[#1e3a5f] shadow-sm">
              <div className="flex items-start gap-4">
                <Target className="w-6 h-6 text-[#1e3a5f] flex-shrink-0 mt-1" />
                <p className="text-gray-700 italic leading-relaxed">
                  "{t('about.mission')}"
                </p>
              </div>
            </div>
          </div>

          {/* Right - Stats Grid with Image */}
          <div className="space-y-8">
            {/* Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-lg">
              <img
                src="https://images.unsplash.com/photo-1562564055-71e051d33c19?w=800&auto=format&fit=crop&q=80"
                alt="Legal consultation"
                className="w-full h-64 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a5f]/20 to-transparent" />
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, index) => {
                const IconComponent = statIcons[index];
                return (
                  <div
                    key={index}
                    className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow"
                  >
                    <div className="w-10 h-10 bg-[#1e3a5f]/10 rounded-xl flex items-center justify-center mb-3">
                      <IconComponent className="w-5 h-5 text-[#1e3a5f]" />
                    </div>
                    <div className="text-2xl font-bold text-[#1e3a5f] mb-1">
                      {stat.value}
                    </div>
                    <div className="text-gray-500 text-xs uppercase tracking-wider">
                      {stat.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
